import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Spinner,
  Text,
} from '@chakra-ui/react';

import { usePriceQuote } from '../../hooks/pricing/usePriceQuote';
import { useSaveQuote } from '../../hooks/pricing/useSaveQuote';
import { useQuoteService } from '../../lib/services/QuoteService';
import { BusinessUnit, UniverseType } from '../../lib/pricing-tool-api';

import LocationInformationSection from '../../components/pricing/LocationInformationSection';
import ClientInformationSection from '../../components/pricing/ClientInformationSectionV2';
import TargetUniverseSection from '../../components/pricing/TargetUniverseSection';
import FeeTypeSection from '../../components/pricing/FeeTypeSection';
import SaveQuoteSection from '../../components/pricing/SaveQuoteSection';
import GenerateQuoteSection from '../../components/pricing/generateQuoteSection/GenerateQuoteSection';
import ResearchComplexitySection from './ResearchComplexitySection';

function PricingFormSection({
  menuName,
  component,
}: {
  menuName: string;
  component: React.ReactNode;
}) {
  return (
    <Box key={`main-section-${menuName}`} w="100%">
      <Text bg="brand.primaryColor" mb={4}>
        {menuName}
      </Text>
      <Box mb={8}>{component}</Box>
    </Box>
  );
}

function PricingTool() {
  const [isLoading, setIsLoading] = React.useState(true);
  const [errMsg, setErrMsg] = React.useState('');
  const [legacyUniverseTranslated, setLegacyUniverseTranslated] =
    React.useState(false);
  const [searchParams] = useSearchParams();
  const existingQuoteIdToEdit =
    searchParams && searchParams.get('id') ? searchParams.get('id') : null;

  const {
    absoluteMarkupOrDiscount,
    calculatedMaxRecommendedSampleSize,
    calculatedGeneralMaxRecommendedSampleSize,
    calculatedDemPrimaryMaxRecommendedSampleSize,
    clientType,
    clientBudget,
    clientComplexity,
    businessUnit,
    districts,
    districtsDemoData,
    isPricingLoading,
    markupOrDiscountPercentage,
    numberOfOversamples,
    numberOfQuestions,
    numOfAdditionalCrosstabs,
    numOfAdditionalWeightingUniverses,
    numOfAdditionalMonitoringUniverses,
    otherUniverseTypeDescription,
    researchComplexity,
    creativeTesting,
    lowBandwidth,
    pricedQuote,
    pricingErrorMessage,
    sampleSizes,
    setAbsoluteMarkupOrDiscount,
    setClientType,
    setClientBudget,
    setClientComplexity,
    setBusinessUnit,
    setClientBudgetReason,
    setDistricts,
    setDistrictsDemoData,
    setMarkupOrDiscountPercentage,
    setNumberOfOversamples,
    setNumberOfQuestions,
    setNumOfAdditionalCrosstabs,
    setNumOfAdditionalWeightingUniverses,
    setNumOfAdditionalMonitoringUniverses,
    setOtherUniverseTypeDescription,
    setResearchComplexity,
    setCreativeTesting,
    setLowBandwidth,
    setSampleSizes,
    setSpecialDemoFilterPct,
    setUniverseTypes,
    setVideoLength,
    specialDemoFilterPct,
    suggestedClientBudgets,
    suggestedClientComplexities,
    showClientBudgetReason,
    clientBudgetReason,
    universeTypes,
    videoLength,
    universeCountIsValid,
    universeCountErrorMessage,
    showOtherUniverseTypeDescription,
    setUserInputtedSpecialDemoFilterPct,
    suggestedSpecialDemoFilterPct,
  } = usePriceQuote();

  const {
    memo,
    dealName,
    dealId,
    firewallSide,
    clientName,
    setMemo,
    setDealName,
    setDealId,
    setFirewallSide,
    setClientName,
    savedQuote,
    setSavedQuote,
    setQuoteErrorMessage,
    isQuoteSaving,
    saveQuote,
    canSaveQuote,
  } = useSaveQuote(pricedQuote);

  const quoteService = useQuoteService();

  useEffect(() => {
    if (existingQuoteIdToEdit) {
      // Load V2 quote for editing
      quoteService
        .getQuoteV2(existingQuoteIdToEdit, true)
        .then((response) => {
          const quote = response.data;
          const request = quote.price_info?.request;

          // Populate the form with the quote data
          setDealName(quote.deal_name);
          setDealId(quote.zendesk_deal_id);
          setClientName(quote.client_name);
          setFirewallSide(quote.firewall_side);
          setMemo(quote.memo || '');

          // Populate pricing form fields from quote.price_info.request
          if (request) {
            // Client information
            setClientType(request.client_type);
            setClientBudget(request.client_budget);
            setClientComplexity(request.client_complexity);
            setBusinessUnit((request as any).business_unit || BusinessUnit.Pna);
            setClientBudgetReason(request.client_budget_reason || '');

            // Research complexity
            setResearchComplexity(request.research_complexity);
            setVideoLength(request.video_length || 0);
            setCreativeTesting(request.creative_testing || false);
            setLowBandwidth(request.low_bandwidth || false);

            // Target universe — translate the legacy combined value into the
            // new local/special bucket so old quotes load cleanly into the new UI.
            const loadedUniverseTypes = request.universe_types || [];
            const hasLegacyPrimaryLocalSpecial = loadedUniverseTypes.includes(
              UniverseType.PrimaryLocalSpecialElection,
            );
            const translatedUniverseTypes = hasLegacyPrimaryLocalSpecial
              ? loadedUniverseTypes.map((u) =>
                  u === UniverseType.PrimaryLocalSpecialElection
                    ? UniverseType.LocalSpecialElection
                    : u,
                )
              : loadedUniverseTypes;
            setUniverseTypes(translatedUniverseTypes);
            setLegacyUniverseTranslated(hasLegacyPrimaryLocalSpecial);
            setNumOfAdditionalCrosstabs(
              request.num_of_additional_crosstabs || 0,
            );
            setNumOfAdditionalWeightingUniverses(
              request.num_of_additional_weighting_universes || 0,
            );
            setNumOfAdditionalMonitoringUniverses(
              request.num_of_additional_monitoring_universes || 0,
            );
            setNumberOfOversamples(request.number_of_oversamples || 0);
            setSpecialDemoFilterPct(request.special_demo_filter_pct || 1.0);
            setUserInputtedSpecialDemoFilterPct(true);
            setOtherUniverseTypeDescription(
              request.other_universe_type_description || '',
            );

            // Sample sizes and questions
            setSampleSizes(request.sample_sizes || []);
            setNumberOfQuestions(request.number_of_questions || []);

            // Fee type
            setAbsoluteMarkupOrDiscount(
              request.absolute_markup_or_discount || 0,
            );
            setMarkupOrDiscountPercentage(
              request.markup_or_discount_percentage || 1.0,
            );

            // Districts (location information)
            setDistricts(request.districts);
            setDistrictsDemoData(quote.district_demo_data);
          }

          // Set the quote data
          setSavedQuote(quote);

          setIsLoading(false);
        })
        .catch(() => {
          setErrMsg(
            `Could not fetch quote with ID ${existingQuoteIdToEdit}. Please contact the tech team with any questions.`,
          );
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [
    existingQuoteIdToEdit,
    quoteService,
    setDealName,
    setDealId,
    setClientName,
    setFirewallSide,
    setMemo,
    setSavedQuote,
    setClientType,
    setClientBudget,
    setClientComplexity,
    setBusinessUnit,
    setClientBudgetReason,
    setResearchComplexity,
    setVideoLength,
    setCreativeTesting,
    setLowBandwidth,
    setUniverseTypes,
    setNumOfAdditionalCrosstabs,
    setNumOfAdditionalWeightingUniverses,
    setNumOfAdditionalMonitoringUniverses,
    setNumberOfOversamples,
    setSpecialDemoFilterPct,
    setOtherUniverseTypeDescription,
    setSampleSizes,
    setNumberOfQuestions,
    setMarkupOrDiscountPercentage,
    setAbsoluteMarkupOrDiscount,
    setDistricts,
  ]);

  return (
    <Box>
      {isLoading && <Spinner />}
      {!isLoading && errMsg && (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{errMsg}</AlertDescription>
        </Alert>
      )}
      {!isLoading && !errMsg && (
        <Box>
          <PricingFormSection
            menuName="Location Information"
            component={
              <LocationInformationSection
                districts={districts}
                districtsDemoData={districtsDemoData}
                setDistricts={setDistricts}
                setDistrictsDemoData={setDistrictsDemoData}
              />
            }
          />
          <PricingFormSection
            menuName="Client Information"
            component={
              <ClientInformationSection
                clientType={clientType}
                clientBudget={clientBudget}
                clientComplexity={clientComplexity}
                businessUnit={businessUnit}
                setClientType={setClientType}
                setClientBudget={setClientBudget}
                setClientComplexity={setClientComplexity}
                setBusinessUnit={setBusinessUnit}
                suggestedClientBudgets={suggestedClientBudgets}
                suggestedClientComplexities={suggestedClientComplexities}
                showClientBudgetReason={
                  showClientBudgetReason
                }
                clientBudgetReason={clientBudgetReason}
                setClientBudgetReason={
                  setClientBudgetReason
                }
              />
            }
          />
          <PricingFormSection
            menuName="Target Universe"
            component={
              <TargetUniverseSection
                numberOfOversamples={numberOfOversamples}
                setNumberOfOversamples={setNumberOfOversamples}
                numOfAdditionalCrosstabs={numOfAdditionalCrosstabs}
                numOfAdditionalWeightingUniverses={
                  numOfAdditionalWeightingUniverses
                }
                numOfAdditionalMonitoringUniverses={
                  numOfAdditionalMonitoringUniverses
                }
                setNumOfAdditionalCrosstabs={setNumOfAdditionalCrosstabs}
                setNumOfAdditionalWeightingUniverses={
                  setNumOfAdditionalWeightingUniverses
                }
                setNumOfAdditionalMonitoringUniverses={
                  setNumOfAdditionalMonitoringUniverses
                }
                otherUniverseTypeDescription={otherUniverseTypeDescription}
                setOtherUniverseTypeDescription={
                  setOtherUniverseTypeDescription
                }
                specialDemoFilterPct={specialDemoFilterPct}
                setSpecialDemoFilterPct={setSpecialDemoFilterPct}
                universeTypes={universeTypes}
                setUniverseTypes={setUniverseTypes}
                universeCountIsValid={universeCountIsValid}
                universeCountErrorMessage={universeCountErrorMessage}
                showOtherUniverseTypeDescription={
                  showOtherUniverseTypeDescription
                }
                setUserInputtedSpecialDemoFilterPct={
                  setUserInputtedSpecialDemoFilterPct
                }
                suggestedSpecialDemoFilterPct={suggestedSpecialDemoFilterPct}
                legacyUniverseTranslated={legacyUniverseTranslated}
              />
            }
          />
          <PricingFormSection
            menuName="Poll Information"
            component={
              <ResearchComplexitySection
                researchComplexity={researchComplexity}
                setResearchComplexity={setResearchComplexity}
                videoLength={videoLength}
                setVideoLength={setVideoLength}
                creativeTesting={creativeTesting}
                setCreativeTesting={setCreativeTesting}
                lowBandwidth={lowBandwidth}
                setLowBandwidth={setLowBandwidth}
              />
            }
          />
          <PricingFormSection
            menuName="Fee Type"
            component={
              <FeeTypeSection
                markupOrDiscountPercentage={markupOrDiscountPercentage}
                setMarkupOrDiscountPercentage={setMarkupOrDiscountPercentage}
                absoluteMarkupOrDiscount={absoluteMarkupOrDiscount}
                setAbsoluteMarkupOrDiscount={setAbsoluteMarkupOrDiscount}
              />
            }
          />
          <PricingFormSection
            menuName="Generate Quote"
            component={
              <GenerateQuoteSection
                numberOfQuestions={numberOfQuestions}
                setNumberOfQuestions={setNumberOfQuestions}
                sampleSizes={sampleSizes}
                setSampleSizes={setSampleSizes}
                districts={districts}
                maxRecommendedSampleSize={calculatedMaxRecommendedSampleSize}
                generalMaxRecommendedSampleSize={
                  calculatedGeneralMaxRecommendedSampleSize
                }
                demPrimaryMaxRecommendedSampleSize={
                  calculatedDemPrimaryMaxRecommendedSampleSize
                }
                pricingErrorMessage={pricingErrorMessage}
                isPricingLoading={isPricingLoading}
                pricedQuote={pricedQuote}
                clientType={clientType}
                clientBudget={clientBudget}
                clientComplexity={clientComplexity}
                researchComplexity={researchComplexity}
                universeTypes={universeTypes}
              />
            }
          />
          <PricingFormSection
            menuName="Save Quote"
            component={
              <SaveQuoteSection
                dealId={dealId}
                dealName={dealName}
                clientName={clientName}
                firewallSide={firewallSide}
                memo={memo}
                setMemo={setMemo}
                setDealName={setDealName}
                setDealId={setDealId}
                setFirewallSide={setFirewallSide}
                setClientName={setClientName}
                quoteErrorMessage={pricingErrorMessage}
                setQuoteErrorMessage={setQuoteErrorMessage}
                priceInfo={pricedQuote}
                savedQuote={savedQuote}
                isQuoteSaving={isQuoteSaving}
                saveQuote={saveQuote}
                canSaveQuote={canSaveQuote}
                existingQuote={savedQuote}
              />
            }
          />
        </Box>
      )}
    </Box>
  );
}

export default PricingTool;
